import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Button from '../../common/Button';
import { CONTRACT_TYPES } from '../../../constants/statuses';
import { validateRequired } from '../../../utils/validators';
import systemTypeService from '../../../services/systemTypeService';

const EditContractModal = ({ isOpen, onClose, onSubmit, contract }) => {
  const [formData, setFormData] = useState({
    value: '',
    type: '',
    start_date: '',
    end_date: '',
    services: {
      comprehensive: [],
      non_comprehensive: []
    }
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
  systemTypeService.getProblemMapping().then(result => {
    if (result.success) setAvailableServices(Object.keys(result.data));
  });
  }, []);

  useEffect(() => {
    if (contract) {
      setFormData({
        value: (contract.value ?? '').toString(),
        type: contract.type || contract.contract_type || '',
        start_date: contract.start_date ? contract.start_date.split('T')[0] : '',
        end_date: contract.end_date ? contract.end_date.split('T')[0] : '',
        services: contract.services && typeof contract.services === 'object' && !Array.isArray(contract.services) 
          ? {
              comprehensive: contract.services.comprehensive || [],
              non_comprehensive: contract.services.non_comprehensive || []
            }
          : {
              comprehensive: Array.isArray(contract.services) ? contract.services : [],
              non_comprehensive: []
            }
      });
    }
  }, [contract]);

  // Auto-calculate contract type based on services
  useEffect(() => {
    const hasComp = formData.services.comprehensive.length > 0;
    const hasNonComp = formData.services.non_comprehensive.length > 0;
    
    let newType = 'CAMC'; // Default
    if (hasComp && hasNonComp) {
      newType = 'Both';
    } else if (hasComp) {
      newType = 'CAMC';
    } else if (hasNonComp) {
      newType = 'NCAMC';
    }
    
    if (formData.type && formData.type !== newType) {
      setFormData(prev => ({ ...prev, type: newType }));
    }
  }, [formData.services]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.value)) {
      newErrors.value = 'Contract value is required';
    } else if (isNaN(formData.value) || parseFloat(formData.value) < 0) {
      newErrors.value = 'Please enter a valid non-negative amount';
    }
    if (!validateRequired(formData.start_date)) {
      newErrors.start_date = 'Start date is required';
    }
    if (!validateRequired(formData.end_date)) {
      newErrors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
      newErrors.end_date = 'End date must be after start date';
    }
    
    const totalServices = formData.services.comprehensive.length + formData.services.non_comprehensive.length;
    if (totalServices === 0) {
      newErrors.services = 'At least one service must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setSubmitting(true);

    try {
      const contractData = {
        ...formData,
        value: parseFloat(formData.value)
      };
      
      await onSubmit(contract.id, contractData);
      onClose();
    } catch (error) {
      console.error('Error updating contract:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleServiceToggle = (category, service) => {
    setFormData((prev) => {
      const currentServices = [...prev.services[category]];
      const updatedServices = currentServices.includes(service)
        ? currentServices.filter(s => s !== service)
        : [...currentServices, service];
        
      return { 
        ...prev, 
        services: {
          ...prev.services,
          [category]: updatedServices
        }
      };
    });

    if (errors.services) {
      setErrors((prev) => ({ ...prev, services: '' }));
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      onClose();
    }
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update Contract'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Contract ${contract?.contract_id || contract?.id}`}
      size="large"
      footer={footer}
    >
      <form onSubmit={handleSubmit}>
        <div style={gridStyle}>
          <Input
            label="Contract Value (₹)"
            name="value"
            type="number"
            placeholder="e.g., 500000"
            value={formData.value}
            onChange={handleChange}
            error={errors.value}
            required
          />

          <Input
            label="Contract Type (Auto-calculated)"
            name="type"
            value={formData.type}
            disabled
            placeholder="Select services to determine type"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Services Covered <span style={{ color: 'red' }}>*</span>
          </label>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Comprehensive Section */}
            <div>
              <h5 style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>Comprehensive Services</h5>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '8px',
                padding: '12px',
                background: 'rgba(16, 185, 129, 0.05)',
                borderRadius: '8px',
                border: errors.services ? '1px solid #ef4444' : '1px solid var(--border)'
              }}>
                {availableServices.map(service => (
                  <label key={`comp-${service}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={formData.services.comprehensive.includes(service)}
                      onChange={() => handleServiceToggle('comprehensive', service)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Non-Comprehensive Section */}
            <div>
              <h5 style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>Non-Comprehensive Services</h5>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '8px',
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.05)',
                borderRadius: '8px',
                border: errors.services ? '1px solid #ef4444' : '1px solid var(--border)'
              }}>
                {availableServices.map(service => (
                  <label key={`noncomp-${service}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={formData.services.non_comprehensive.includes(service)}
                      onChange={() => handleServiceToggle('non_comprehensive', service)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{service}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {errors.services && (
            <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.services}</span>
          )}
        </div>

        <div style={gridStyle}>
          <Input
            label="Start Date"
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleChange}
            error={errors.start_date}
            required
          />

          <Input
            label="End Date"
            name="end_date"
            type="date"
            value={formData.end_date}
            onChange={handleChange}
            error={errors.end_date}
            required
          />
        </div>

        <div style={{
          padding: '16px',
          background: 'var(--surface-light)',
          borderRadius: '8px',
          fontSize: '13px',
          color: 'var(--text-secondary)',
          marginTop: '16px',
          border: '1px solid var(--border)'
        }}>
           <strong>Warning:</strong> Changing dates may affect existing tickets and SLA calculations.
        </div>
      </form>
    </Modal>
  );
};

export default EditContractModal;